import type { DataChoice, Localization } from '../types'

import React from 'react'
import { CheckIcon, ChevronsUpDownIcon, LoaderCircleIcon } from 'lucide-react'

import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from '@/shared/components/ui/command'
import {
	InputBase,
	InputBaseAdornment,
	InputBaseControl,
	InputBaseFlexWrapper,
} from '@/shared/components/ui/input-base'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/ui/popover'
import {
	Virtualized,
	VirtualizedVirtualizer,
} from '@/shared/components/ui/virtualizer'
import { cn } from '@/shared/lib/utils'

type FieldSelectProps = Omit<
	React.ComponentProps<typeof InputBase>,
	'onChange'
> & {
	initialOpen?: boolean
	placeholder: string
	className?: string
	invalid?: boolean | string
	disabled?: boolean
	value?: string
	onChange(value: string): void
	options: DataChoice[]
	isLoading?: boolean
	locale: Localization
}
export default function SelectField({
	isLoading,
	invalid,
	options,
	value,
	onChange,
	disabled,
	placeholder,
	initialOpen,
	className,
	locale,
	...props
}: FieldSelectProps) {
	const [open, setOpen] = React.useState(initialOpen)

	const selectedValue = React.useMemo(
		() => options.find((option) => option.value === value),
		[value, options]
	)
	const isWrongValue = React.useMemo(
		() => Boolean(value && !selectedValue),
		[value, selectedValue]
	)

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild disabled={disabled} className={className}>
				<InputBase
					asChild
					role='combobox'
					aria-expanded={open}
					aria-invalid={!!invalid || isWrongValue}
					disabled={disabled || isLoading}
					{...props}
				>
					<button disabled={disabled || isLoading}>
						<InputBaseControl>
							<InputBaseFlexWrapper className='truncate'>
								{selectedValue ? (
									<div className='flex items-center gap-2 truncate'>
										<span className='truncate'>{selectedValue.label}</span>
									</div>
								) : value ? (
									<div className='truncate text-destructive'>{value}</div>
								) : (
									<div className='truncate text-placeholder'>{placeholder}</div>
								)}
							</InputBaseFlexWrapper>
						</InputBaseControl>
						<InputBaseAdornment>
							{isLoading ? (
								<LoaderCircleIcon className='size-4 shrink-0 animate-spin opacity-50' />
							) : (
								<ChevronsUpDownIcon className='size-4 shrink-0 opacity-50' />
							)}
						</InputBaseAdornment>
					</button>
				</InputBase>
			</PopoverTrigger>
			<PopoverContent className='w-(--radix-popover-trigger-width) p-0'>
				<SelectVirtualContent
					locale={locale}
					value={value}
					onChange={(value) => {
						onChange(value)
						setOpen(false)
					}}
					options={options}
				/>
			</PopoverContent>
		</Popover>
	)
}

function SelectVirtualContent<T extends DataChoice>({
	options,
	onChange,
	value,
	locale,
}: {
	locale: Localization
	options: T[]
	onChange: (value: string) => void
	value?: string
}) {
	const [searchText, setSearchText] = React.useState('')
	const filteredOptions = options.filter((option) =>
		option.label.toLowerCase().includes(searchText.toLowerCase() ?? [])
	)

	return (
		<Command>
			<CommandInput
				onValueChange={setSearchText}
				value={searchText}
				placeholder={locale?.getSearchPlaceholder?.() || 'Input to search'}
			/>
			<Virtualized asChild>
				<CommandList className='max-h-[400px]'>
					<CommandEmpty>No options found.</CommandEmpty>
					<CommandGroup>
						<VirtualizedVirtualizer>
							{filteredOptions.map((option) => (
								<CommandItem
									key={option.value}
									keywords={[option.label]}
									value={option.value}
									onSelect={onChange}
								>
									{option.label}
									<CheckIcon
										className={cn(
											'ml-auto',
											value === option.value ? 'opacity-100' : 'opacity-0'
										)}
									/>
								</CommandItem>
							))}
						</VirtualizedVirtualizer>
					</CommandGroup>
				</CommandList>
			</Virtualized>
		</Command>
	)
}
